import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Building2 } from "lucide-react";
import { sendPasswordResetEmail } from "firebase/auth";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import { collection, getDocs, query, where, doc, getDoc } from "firebase/firestore";
import { db, auth as firebaseAuth } from "../firebase";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [playIntroAnimation, setPlayIntroAnimation] = useState(true);

  const auth = useAuth();
  const companyContext = useCompany();
  const navigate = useNavigate();

  const normalizedEmail = useMemo(
    () => String(email || "").trim().toLowerCase(),
    [email]
  );

  const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

  const emailToDocId = (email) => {
    return email.replace(/\./g, "_");
  };

  const clearCompanyStorage = () => {
    const keys = [
      "selectedCompany",
      "currentCompany",
      "selectedCompanyId",
      "companyId",
      "companyAccountUid",
      "company",
      "activeCompany",
      "activeCompanyId",
    ];

    keys.forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });

    if (typeof auth?.setSelectedCompany === "function") {
      auth.setSelectedCompany(null);
    }

    if (typeof companyContext?.setSelectedCompany === "function") {
      companyContext.setSelectedCompany(null);
    }

    if (typeof companyContext?.setCurrentCompany === "function") {
      companyContext.setCurrentCompany(null);
    }

    if (typeof companyContext?.clearCompany === "function") {
      companyContext.clearCompany();
    }
  };

  const signOutSafely = async () => {
    try {
      if (typeof auth?.logout === "function") {
        await auth.logout();
      } else if (typeof auth?.signOut === "function") {
        await auth.signOut();
      } else if (typeof auth?.signout === "function") {
        await auth.signout();
      }
    } catch (err) {
      console.warn("Kunde inte logga ut säkert:", err);
    }
  };

  const translateRole = (role) => {
    const normalizedRole = String(role || "").trim().toLowerCase();

    const roleMap = {
      admin: "Administratör",
      "super-admin": "Super-administratör",
      superadmin: "Super-administratör",
      manager: "Chef",
      user: "Användare",
    };

    return roleMap[normalizedRole] || role || "Okänd roll";
  };

  const isAdminRole = (role) => {
    const normalizedRole = String(role || "").trim().toLowerCase();

    return (
      normalizedRole === "admin" ||
      normalizedRole === "super-admin" ||
      normalizedRole === "superadmin"
    );
  };

  const isActiveMembership = (data = {}, requireExplicitStatus = false) => {
    const status = String(data.membershipStatus || data.status || "")
      .trim()
      .toLowerCase();

    if (data.isActive === false) return false;
    if (!status) return !requireExplicitStatus;
    return status === "active";
  };

  const searchEmployedAdmins = async (loginEmail) => {
    const cleanEmail = normalizeEmail(loginEmail);
    const emailDocId = emailToDocId(cleanEmail);

    const usersRef = collection(db, "users");
    const allUsersSnapshot = await getDocs(usersRef);

    const foundEmployments = [];

    for (const userDoc of allUsersSnapshot.docs) {
      const userData = userDoc.data();
      const employedRef = collection(db, "users", userDoc.id, "employed");
      const employedDocRef = doc(employedRef, emailDocId);
      const employedDoc = await getDoc(employedDocRef);

      if (employedDoc.exists()) {
        const employedData = employedDoc.data();
        const role = String(employedData.role || "").trim().toLowerCase();

        if (
          isAdminRole(role) &&
          isActiveMembership(employedData, true)
        ) {
          foundEmployments.push({
            userDocId: userDoc.id,
            employedDocId: emailDocId,
            companyId: userData.companyId || userData.companyAccountUid || userDoc.id,
            companyAccountUid: userData.companyAccountUid || userData.companyId || userDoc.id,
            companyName:
              userData.companyName ||
              userData.name ||
              userData.businessName ||
              "Okänt företag",
            role,
            roleLabel: translateRole(role),
            email: cleanEmail,
            employedData,
            userData,
            isEmployedAdmin: true,
          });
        }
      }
    }

    return foundEmployments;
  };

  const fetchUserCompanies = async (loginEmail) => {
    const cleanEmail = normalizeEmail(loginEmail);
    const usersRef = collection(db, "users");

    const emailValuesToSearch = Array.from(
      new Set([cleanEmail, String(loginEmail || "").trim()])
    ).filter(Boolean);

    const docsById = new Map();

    for (const emailValue of emailValuesToSearch) {
      const userQuery = query(usersRef, where("email", "==", emailValue));
      const snapshot = await getDocs(userQuery);

      snapshot.forEach((userDoc) => {
        docsById.set(userDoc.id, userDoc);
      });
    }

    const userCompanies = [];

    docsById.forEach((userDoc) => {
      const userData = userDoc.data() || {};
      const role = String(userData.role || "").trim().toLowerCase();

      if (!isAdminRole(role)) return;
      if (!isActiveMembership(userData)) return;

      const companyId = userData.companyId || userData.companyAccountUid || userDoc.id;
      const companyAccountUid = userData.companyAccountUid || userData.companyId || userDoc.id;

      userCompanies.push({
        id: `${companyId}-${userDoc.id}`,
        userDocId: userDoc.id,
        companyId,
        companyName:
          userData.companyName ||
          userData.name ||
          userData.businessName ||
          "Okänt företag",
        role,
        roleLabel: translateRole(role),
        email: cleanEmail,
        companyAccountUid,
        uid: companyAccountUid,
        employedData: userData,
        userData,
        isEmployedAdmin: false,
      });
    });

    const employedAdmins = await searchEmployedAdmins(cleanEmail);

    for (const employedAdmin of employedAdmins) {
      const alreadyExists = userCompanies.some(
        (uc) => uc.companyId === employedAdmin.companyId
      );

      if (!alreadyExists) {
        userCompanies.push(employedAdmin);
      }
    }

    return userCompanies.filter(
      (company, index, self) =>
        index === self.findIndex((item) => item.companyId === company.companyId)
    );
  };

  const persistSelectedCompany = (company, loginEmail) => {
    const companyData = {
      id: company.id,
      userDocId: company.userDocId,
      companyId: company.companyId,
      companyName: company.companyName,
      role: company.role,
      roleLabel: company.roleLabel,
      companyAccountUid: company.companyAccountUid,
      uid: company.companyAccountUid || company.companyId,
      email: loginEmail,
      employedData: company.employedData,
      userData: company.userData,
      isEmployedAdmin: company.isEmployedAdmin || false,
      employedDocId: company.employedDocId,
    };

    const companyJson = JSON.stringify(companyData);

    localStorage.setItem("selectedCompany", companyJson);
    sessionStorage.setItem("selectedCompany", companyJson);

    localStorage.setItem("currentCompany", companyJson);
    sessionStorage.setItem("currentCompany", companyJson);

    localStorage.setItem("selectedCompanyId", companyData.companyId);
    sessionStorage.setItem("selectedCompanyId", companyData.companyId);

    localStorage.setItem("companyId", companyData.companyId);
    sessionStorage.setItem("companyId", companyData.companyId);

    localStorage.setItem("companyAccountUid", companyData.companyAccountUid);
    sessionStorage.setItem("companyAccountUid", companyData.companyAccountUid);

    if (remember) {
      localStorage.setItem("rememberEmail", loginEmail);
      localStorage.setItem("rememberMe", "true");
    } else {
      localStorage.removeItem("rememberEmail");
      localStorage.removeItem("rememberMe");
    }

    if (typeof auth?.setSelectedCompany === "function") {
      auth.setSelectedCompany(companyData);
    }

    if (typeof companyContext?.setSelectedCompany === "function") {
      companyContext.setSelectedCompany(companyData);
    }

    if (typeof companyContext?.setCurrentCompany === "function") {
      companyContext.setCurrentCompany(companyData);
    }

    return companyData;
  };

  const goToDashboard = () => {
    navigate("/dashboard", { replace: true });
  };

  const saveSelectedCompany = async (company, loginEmail) => {
    if (companyLoading) return;

    setError("");
    setCompanyLoading(true);

    try {
      const companyData = persistSelectedCompany(company, loginEmail);

      if (typeof companyContext?.switchCompany === "function") {
        try {
          await companyContext.switchCompany(companyData);
        } catch (switchError) {
          console.warn("switchCompany misslyckades men login fortsätter:", switchError);
        }
      }

      setShowCompanyModal(false);
      setCompanies([]);

      goToDashboard();
    } catch (err) {
      console.error("Fel vid företagsval:", err);
      setError(err?.message || "Kunde inte välja företag. Försök igen.");
    } finally {
      setCompanyLoading(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPlayIntroAnimation(false);
    }, 1200);

    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberEmail");
    const savedRemember = localStorage.getItem("rememberMe") === "true";

    if (savedRemember && savedEmail) {
      setEmail(savedEmail);
      setRemember(true);
    }
  }, []);

  const getLoginErrorMessage = (err) => {
    switch (err?.code) {
      case "auth/user-not-found":
      case "auth/wrong-password":
      case "auth/invalid-credential":
        return "Fel e-postadress eller lösenord";
      case "auth/too-many-requests":
        return "För många inloggningsförsök. Försök igen senare.";
      case "auth/user-disabled":
        return "Detta konto är inaktiverat. Kontakta support.";
      case "auth/invalid-email":
        return "Ogiltig e-postadress";
      case "permission-denied":
        return "Du saknar behörighet att läsa företagsdata.";
      default:
        return err?.message || "Kunde inte logga in";
    }
  };

  const openForgotPasswordModal = () => {
    setError("");
    setResetMessage("");
    setResetEmail(email);
    setShowForgotModal(true);
  };

  const closeForgotPasswordModal = () => {
    if (resetLoading) return;

    setShowForgotModal(false);
    setResetEmail("");
    setResetMessage("");
    setError("");
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (resetLoading) return;

    setError("");
    setResetMessage("");

    const cleanEmail = normalizeEmail(resetEmail);

    if (!cleanEmail) {
      setError("Skriv in din e-postadress.");
      return;
    }

    setResetLoading(true);

    try {
      await sendPasswordResetEmail(firebaseAuth, cleanEmail);
      setResetMessage("Vi har skickat en länk för att återställa lösenordet.");
    } catch (err) {
      console.error("Fel vid återställning av lösenord:", err);

      if (err?.code === "auth/invalid-email") {
        setError("Ogiltig e-postadress.");
      } else if (err?.code === "auth/user-not-found") {
        setError("Det finns inget konto med den e-postadressen.");
      } else if (err?.code === "auth/too-many-requests") {
        setError("För många försök. Försök igen senare.");
      } else {
        setError("Kunde inte skicka återställningslänk. Försök igen.");
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading || companyLoading) return;

    setError("");
    setResetMessage("");
    setLoading(true);
    setShowCompanyModal(false);
    setCompanies([]);

    try {
      if (!normalizedEmail || !password) {
        throw new Error("Fyll i e-postadress och lösenord.");
      }

      if (typeof auth?.signIn !== "function") {
        throw new Error("AuthContext saknar signIn-funktion.");
      }

      clearCompanyStorage();

      await auth.signIn(normalizedEmail, password, remember);

      const userCompanies = await fetchUserCompanies(normalizedEmail);

      if (userCompanies.length === 0) {
        clearCompanyStorage();
        await signOutSafely();
        setError("Du har inte behörighet att logga in på något företag.");
        return;
      }

      if (userCompanies.length === 1) {
        await saveSelectedCompany(userCompanies[0], normalizedEmail);
        return;
      }

      setCompanies(userCompanies);
      setShowCompanyModal(true);
    } catch (err) {
      console.error("Inloggningsfel:", err);
      clearCompanyStorage();
      setError(getLoginErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = async () => {
    if (companyLoading) return;

    setShowCompanyModal(false);
    setCompanies([]);
    clearCompanyStorage();
    await signOutSafely();
  };

  const AnimatedLetters = () => {
    const letters = ["L", "U", "N", "E", "R", "O"];

    return (
      <div style={styles.logoTextContainer}>
        {letters.map((letter, index) => (
          <span
            key={letter}
            style={{
              ...styles.animatedLetter,
              ...(playIntroAnimation
                ? {
                    opacity: 0,
                    animation: `letterBounce 0.4s ease-out ${
                      index * 0.1
                    }s forwards`,
                  }
                : {
                    opacity: 1,
                    transform: "translateY(0) rotate(0deg)",
                  }),
            }}
          >
            {letter}
          </span>
        ))}
      </div>
    );
  };

  const CompanyModal = () => (
    <div style={styles.modalOverlay} onClick={handleCloseModal}>
      <div style={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalContent}>
          <div style={styles.modalIconContainer}>
            <Building2 size={30} color="#6366f1" />
          </div>

          <h2 style={styles.modalTitle}>Välj företag</h2>

          <p style={styles.modalSubtitle}>
            Du är administratör i flera företag. Välj vilket företag du vill
            logga in på.
          </p>
        </div>

        <div style={styles.companiesList}>
          {companies.map((company) => (
            <button
              key={company.id}
              type="button"
              disabled={companyLoading}
              onClick={() => saveSelectedCompany(company, normalizedEmail)}
              style={{
                ...styles.companyButton,
                opacity: companyLoading ? 0.6 : 1,
                cursor: companyLoading ? "not-allowed" : "pointer",
              }}
            >
              <div style={styles.companyInfo}>
                <div style={styles.companyName}>{company.companyName}</div>

                <div
                  style={{
                    ...styles.companyRole,
                    backgroundColor:
                      company.role === "super-admin" ||
                      company.role === "superadmin"
                        ? "#fff3e0"
                        : "#f0edff",
                    color:
                      company.role === "super-admin" ||
                      company.role === "superadmin"
                        ? "#e67e22"
                        : "#6366f1",
                  }}
                >
                  {company.roleLabel}
                </div>
              </div>

              <div style={styles.companyArrow}>→</div>
            </button>
          ))}
        </div>

        {error ? <div style={styles.modalErrorMessage}>{error}</div> : null}

        <button
          type="button"
          disabled={companyLoading}
          onClick={handleCloseModal}
          style={{
            ...styles.modalCancelButton,
            opacity: companyLoading ? 0.6 : 1,
            cursor: companyLoading ? "not-allowed" : "pointer",
          }}
        >
          {companyLoading ? "Väljer företag..." : "Avbryt"}
        </button>
      </div>
    </div>
  );

  const ForgotPasswordModal = () => (
    <div style={styles.modalOverlay} onClick={closeForgotPasswordModal}>
      <div style={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalContent}>
          <h2 style={styles.modalTitle}>Glömt lösenord?</h2>

          <p style={styles.modalSubtitle}>
            Skriv in din e-postadress så skickar vi en länk för att återställa
            lösenordet.
          </p>
        </div>

        <form onSubmit={handleForgotPassword}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>E-post</label>
            <input
              type="email"
              placeholder="din@email.se"
              value={resetEmail}
              autoComplete="email"
              onChange={(e) => setResetEmail(e.target.value)}
              required
              style={styles.input}
            />
          </div>

          {error ? <div style={styles.modalErrorMessage}>{error}</div> : null}

          {resetMessage ? (
            <div style={styles.successMessage}>{resetMessage}</div>
          ) : null}

          {!resetMessage ? (
            <button
              type="submit"
              disabled={resetLoading}
              style={{
                ...styles.submitButton,
                marginTop: 16,
                opacity: resetLoading ? 0.6 : 1,
                cursor: resetLoading ? "not-allowed" : "pointer",
              }}
            >
              {resetLoading ? "Skickar..." : "Skicka återställningslänk"}
            </button>
          ) : null}

          <button
            type="button"
            disabled={resetLoading}
            onClick={closeForgotPasswordModal}
            style={{
              ...styles.modalCancelButton,
              opacity: resetLoading ? 0.6 : 1,
              cursor: resetLoading ? "not-allowed" : "pointer",
            }}
          >
            Stäng
          </button>
        </form>
      </div>
    </div>
  );

  return (
    <>
      <div style={styles.container}>
        <div style={styles.backgroundContainer}>
          <div style={{ ...styles.bubble, ...styles.bubble1 }} />
          <div style={{ ...styles.bubble, ...styles.bubble2 }} />
          <div style={{ ...styles.bubble, ...styles.bubble3 }} />
          <div style={{ ...styles.bubble, ...styles.bubble4 }} />
          <div style={{ ...styles.bubble, ...styles.bubble5 }} />
          <div style={{ ...styles.bubble, ...styles.bubble6 }} />
          <div style={{ ...styles.bubble, ...styles.bubble7 }} />
          <div style={{ ...styles.bubble, ...styles.bubble8 }} />
          <div style={{ ...styles.bubble, ...styles.bubble9 }} />
          <div style={{ ...styles.bubble, ...styles.bubble10 }} />
          <div style={styles.gradientOverlay} />
        </div>

        <div
          style={{
            ...styles.loginCard,
            animation: playIntroAnimation ? "fadeInUp 0.6s ease-out" : "none",
          }}
        >
          <div style={styles.logoContainer}>
            <AnimatedLetters />
            <p style={styles.subtitleText}>
              smart schemaläggning och rapportering
            </p>
          </div>

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>E-post</label>
              <input
                type="email"
                placeholder="Email"
                value={email}
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
                required
                style={styles.input}
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Lösenord</label>
              <div style={styles.passwordWrapper}>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Lösenord"
                  value={password}
                  autoComplete="current-password"
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={styles.passwordInput}
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  style={styles.eyeButton}
                  aria-label={showPassword ? "Dölj lösenord" : "Visa lösenord"}
                >
                  {showPassword ? (
                    <EyeOff size={18} color="#6366f1" />
                  ) : (
                    <Eye size={18} color="#6366f1" />
                  )}
                </button>
              </div>

              <button
                type="button"
                onClick={openForgotPasswordModal}
                disabled={loading || companyLoading}
                style={{
                  ...styles.forgotPasswordButton,
                  opacity: loading || companyLoading ? 0.6 : 1,
                  cursor: loading || companyLoading ? "not-allowed" : "pointer",
                }}
              >
                Glömt lösenord?
              </button>
            </div>

            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                style={styles.checkbox}
              />
              <span style={styles.checkboxText}>Kom ihåg mig</span>
            </label>

            <button
              type="submit"
              disabled={loading || companyLoading}
              style={{
                ...styles.submitButton,
                opacity: loading || companyLoading ? 0.6 : 1,
                cursor: loading || companyLoading ? "not-allowed" : "pointer",
              }}
            >
              {loading || companyLoading ? "Loggar in..." : "Logga in"}
            </button>

            <div style={styles.errorWrapper}>
              {!showCompanyModal && !showForgotModal && error ? (
                <div style={styles.errorMessage}>{error}</div>
              ) : null}
            </div>
          </form>
        </div>
      </div>

      {showCompanyModal ? <CompanyModal /> : null}
      {showForgotModal ? <ForgotPasswordModal /> : null}

      <style>
        {`
          * {
            box-sizing: border-box;
          }

          @keyframes letterBounce {
            0% {
              opacity: 0;
              transform: translateY(30px) rotate(-20deg);
            }
            50% {
              transform: translateY(-8px) rotate(3deg);
            }
            100% {
              opacity: 1;
              transform: translateY(0) rotate(0deg);
            }
          }

          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          input,
          button,
          textarea,
          select {
            font: inherit;
          }

          input:focus {
            border-color: #6366f1 !important;
          }
        `}
      </style>
    </>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "Inter, Arial, sans-serif",
    position: "relative",
    overflow: "hidden",
    backgroundColor: "#f8fafc",
    padding: "24px",
  },

  backgroundContainer: {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
  },

  gradientOverlay: {
    position: "absolute",
    inset: 0,
    backgroundColor: "rgba(248, 250, 252, 0.85)",
  },

  bubble: {
    position: "absolute",
    borderRadius: "50%",
    opacity: 0.4,
    pointerEvents: "none",
  },

  bubble1: {
    width: 120,
    height: 120,
    backgroundColor: "#6366f1",
    top: "10%",
    left: -30,
  },

  bubble2: {
    width: 180,
    height: 180,
    backgroundColor: "#8b5cf6",
    top: "20%",
    right: -50,
  },

  bubble3: {
    width: 90,
    height: 90,
    backgroundColor: "#a78bfa",
    bottom: "30%",
    left: "10%",
  },

  bubble4: {
    width: 200,
    height: 200,
    backgroundColor: "#c084fc",
    bottom: "15%",
    right: -60,
  },

  bubble5: {
    width: 70,
    height: 70,
    backgroundColor: "#6366f1",
    top: "50%",
    left: "20%",
  },

  bubble6: {
    width: 150,
    height: 150,
    backgroundColor: "#8b5cf6",
    top: "70%",
    left: "60%",
  },

  bubble7: {
    width: 50,
    height: 50,
    backgroundColor: "#a78bfa",
    top: "15%",
    left: "50%",
  },

  bubble8: {
    width: 110,
    height: 110,
    backgroundColor: "#c084fc",
    bottom: "40%",
    right: "15%",
  },

  bubble9: {
    width: 80,
    height: 80,
    backgroundColor: "#6366f1",
    top: "85%",
    left: "5%",
  },

  bubble10: {
    width: 130,
    height: 130,
    backgroundColor: "#8b5cf6",
    bottom: "10%",
    left: "40%",
  },

  loginCard: {
    width: "100%",
    maxWidth: 430,
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(10px)",
    borderRadius: 24,
    padding: 40,
    boxShadow: "0 20px 50px rgba(31, 41, 55, 0.1)",
    border: "1px solid rgba(255, 255, 255, 0.7)",
    position: "relative",
    zIndex: 2,
  },

  logoContainer: {
    textAlign: "center",
    marginBottom: 40,
    minHeight: 98,
  },

  logoTextContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 12,
    gap: 4,
    minHeight: 58,
  },

  animatedLetter: {
    fontSize: 48,
    fontWeight: 800,
    color: "#6366f1",
    letterSpacing: 4,
    textTransform: "uppercase",
    textShadow: "0 2px 4px rgba(99, 102, 241, 0.3)",
    display: "inline-block",
    willChange: "transform, opacity",
  },

  subtitleText: {
    fontSize: 14,
    fontWeight: 500,
    color: "#64748b",
    letterSpacing: 0.3,
    textAlign: "center",
    margin: 0,
    minHeight: 20,
  },

  form: {
    width: "100%",
  },

  inputGroup: {
    marginBottom: 20,
  },

  label: {
    fontSize: 14,
    fontWeight: 600,
    color: "#334155",
    marginBottom: 8,
    display: "block",
    letterSpacing: 0.3,
  },

  input: {
    width: "100%",
    height: 52,
    padding: "14px 16px",
    borderRadius: 12,
    border: "1.5px solid #e2e8f0",
    fontSize: 16,
    backgroundColor: "white",
    transition: "border-color 0.2s",
    color: "#1e293b",
    fontWeight: 500,
    outline: "none",
  },

  passwordWrapper: {
    position: "relative",
    width: "100%",
  },

  passwordInput: {
    width: "100%",
    height: 52,
    padding: "14px 52px 14px 16px",
    borderRadius: 12,
    border: "1.5px solid #e2e8f0",
    fontSize: 16,
    backgroundColor: "white",
    transition: "border-color 0.2s",
    color: "#1e293b",
    fontWeight: 500,
    outline: "none",
  },

  eyeButton: {
    position: "absolute",
    right: 10,
    top: "50%",
    transform: "translateY(-50%)",
    width: 36,
    height: 36,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  forgotPasswordButton: {
    marginTop: 10,
    background: "transparent",
    border: "none",
    padding: 0,
    color: "#6366f1",
    fontSize: 14,
    fontWeight: 600,
    textAlign: "right",
    display: "block",
    marginLeft: "auto",
  },

  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 28,
    marginTop: 4,
    cursor: "pointer",
    minHeight: 24,
  },

  checkbox: {
    width: 18,
    height: 18,
    accentColor: "#6366f1",
    cursor: "pointer",
    flexShrink: 0,
  },

  checkboxText: {
    fontSize: 14,
    color: "#475569",
    fontWeight: 500,
  },

  submitButton: {
    width: "100%",
    minHeight: 54,
    border: "none",
    borderRadius: 12,
    padding: "16px",
    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
    color: "#fff",
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: 0.5,
    boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
    transition: "opacity 0.2s ease",
  },

  errorWrapper: {
    minHeight: 56,
    marginTop: 16,
  },

  errorMessage: {
    color: "#e5484d",
    fontSize: 14,
    fontWeight: 500,
    textAlign: "center",
    backgroundColor: "#fff0f0",
    padding: "10px",
    borderRadius: 8,
    border: "1px solid #ffcdcd",
  },

  successMessage: {
    color: "#15803d",
    fontSize: 14,
    fontWeight: 500,
    textAlign: "center",
    backgroundColor: "#f0fdf4",
    padding: "10px",
    borderRadius: 8,
    border: "1px solid #bbf7d0",
    marginTop: 16,
  },

  modalErrorMessage: {
    color: "#e5484d",
    fontSize: 14,
    fontWeight: 500,
    textAlign: "center",
    backgroundColor: "#fff0f0",
    padding: "10px",
    borderRadius: 8,
    border: "1px solid #ffcdcd",
    marginTop: 16,
  },

  modalOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    backdropFilter: "blur(4px)",
    padding: "20px",
  },

  modalContainer: {
    backgroundColor: "white",
    borderRadius: 24,
    maxWidth: 500,
    width: "100%",
    maxHeight: "80vh",
    overflow: "auto",
    padding: 32,
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
    animation: "slideIn 0.3s ease-out",
  },

  modalContent: {
    textAlign: "center",
    marginBottom: 24,
  },

  modalIconContainer: {
    width: 60,
    height: 60,
    backgroundColor: "#f0edff",
    borderRadius: 30,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
  },

  modalTitle: {
    margin: 0,
    marginBottom: 8,
    fontSize: 24,
    color: "#252b42",
  },

  modalSubtitle: {
    margin: 0,
    color: "#8f97ad",
    fontSize: 14,
    lineHeight: 1.5,
  },

  companiesList: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },

  companyButton: {
    width: "100%",
    textAlign: "left",
    padding: "20px",
    borderRadius: 16,
    border: "2px solid #e2e8f0",
    background: "white",
    transition: "all 0.2s ease",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  companyInfo: {
    flex: 1,
  },

  companyName: {
    fontWeight: 700,
    fontSize: 18,
    color: "#252b42",
    marginBottom: 6,
  },

  companyRole: {
    display: "inline-block",
    padding: "4px 12px",
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
  },

  companyArrow: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#f0edff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#6366f1",
    fontSize: 20,
    flexShrink: 0,
  },

  modalCancelButton: {
    width: "100%",
    marginTop: 20,
    padding: "12px",
    borderRadius: 12,
    border: "1px solid #e2e8f0",
    backgroundColor: "white",
    color: "#8f97ad",
    fontSize: 14,
    transition: "all 0.2s ease",
  },
};
